// PaymentModal — collects the payment method, paid amount, and finalises
// the transaction. The actual Firestore write is delegated to a callback
// (`onConfirm`) so this component stays focused on UX.
//
// Phase 6 (piutang, 2026-07-16): added a Lunas / Tempo toggle at the top.
// Tempo = piutang flow — customer takes the goods, pays later (default
// due 14 days). Walk-in customers (no customer bound) cannot go tempo.

import { useEffect, useMemo, useState } from 'react';
import {
  Banknote,
  QrCode,
  Building2,
  AlertTriangle,
  CalendarClock,
  Info,
} from 'lucide-react';
import { addDays, format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FitText } from '@/components/ui/FitText';
import { cn, formatIDR, formatNumber } from '@/lib/format';
import { calculateChange } from '@/lib/pricing';
import type {
  FulfillmentType,
  PaymentMethod,
  PaymentTerms,
} from '@/types/domain';

export interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  grandTotal: number;
  /** Current fulfillment choice so we can mention it in the summary. */
  fulfillment: FulfillmentType;
  /** When true, the parent Checkout is mid-write. Disables confirm. */
  loading?: boolean;
  /** Required to enable Tempo. When null, the Tempo option is disabled. */
  hasCustomer: boolean;
  /** Customer display name for the Tempo summary card. */
  customerName?: string | null;
  /** Called with the chosen payment details. */
  onConfirm: (input: {
    paymentMethod: PaymentMethod;
    paidAmount: number;
    changeAmount: number;
    paymentTerms: PaymentTerms;
    dueDate?: number;
    downPayment?: number;
  }) => Promise<void> | void;
}

const METHODS: Array<{
  value: PaymentMethod;
  label: string;
  description: string;
  icon: typeof Banknote;
}> = [
  {
    value: 'cash',
    label: 'Cash',
    description: 'Uang tunai. Kasir input nominal bayar.',
    icon: Banknote,
  },
  {
    value: 'qris',
    label: 'QRIS',
    description: 'Customer scan QR. Paid = grand total.',
    icon: QrCode,
  },
  {
    value: 'transfer',
    label: 'Transfer',
    description: 'Transfer bank. Paid = grand total.',
    icon: Building2,
  },
];

const QUICK_CASH_AMOUNTS = [50000, 100000, 150000, 200000, 500000];

const TERM_PRESETS = [
  { days: 7, label: '7 hari' },
  { days: 14, label: '14 hari' },
  { days: 30, label: '30 hari' },
] as const;

type DuePreset = (typeof TERM_PRESETS)[number]['days'] | 'custom';
const DEFAULT_DUE_DAYS = 14;

export function PaymentModal({
  open,
  onClose,
  grandTotal,
  fulfillment,
  loading = false,
  hasCustomer,
  customerName,
  onConfirm,
}: PaymentModalProps) {
  const [terms, setTerms] = useState<PaymentTerms>('lunas');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [paidInput, setPaidInput] = useState<string>('');
  const [duePreset, setDuePreset] = useState<DuePreset>(DEFAULT_DUE_DAYS);
  const [customDueDate, setCustomDueDate] = useState<string>(() =>
    format(addDays(new Date(), DEFAULT_DUE_DAYS), 'yyyy-MM-dd'),
  );
  const [downPaymentInput, setDownPaymentInput] = useState<string>('');

  // Reset whenever the modal re-opens. New transaction = fresh state.
  useEffect(() => {
    if (open) {
      setTerms('lunas');
      setMethod('cash');
      setPaidInput('');
      setDuePreset(DEFAULT_DUE_DAYS);
      setCustomDueDate(
        format(addDays(new Date(), DEFAULT_DUE_DAYS), 'yyyy-MM-dd'),
      );
      setDownPaymentInput('');
    }
  }, [open]);

  // If the cart changes customer state from "has customer" to "no customer"
  // while tempo is selected, fall back to lunas (Tempo requires a real
  // customer — see firestore.rules).
  useEffect(() => {
    if (!hasCustomer && terms === 'tempo') {
      setTerms('lunas');
    }
  }, [hasCustomer, terms]);

  // Resolved dueDate (epoch ms). Always in the future (validated below).
  const dueDateMs = useMemo(() => {
    if (terms !== 'tempo') return undefined;
    const base = new Date();
    if (duePreset === 'custom') {
      const parsed = new Date(customDueDate);
      if (Number.isNaN(parsed.getTime())) return undefined;
      // Set to end-of-day so a same-day selection still counts as future.
      parsed.setHours(23, 59, 59, 999);
      return parsed.getTime();
    }
    return addDays(base, duePreset).setHours(23, 59, 59, 999);
  }, [terms, duePreset, customDueDate]);

  // For lunas, paidAmount comes from cash input or implicit (= grandTotal).
  // For tempo, paidAmount = downPayment (DP) at sale time. The
  // outstanding balance is collected later via the Customer page.
  const downPayment = useMemo(() => {
    if (terms !== 'tempo') return 0;
    const raw = Number(downPaymentInput.replace(/[^0-9]/g, '')) || 0;
    return Math.min(raw, grandTotal);
  }, [terms, downPaymentInput, grandTotal]);

  const paidAmount = useMemo(() => {
    if (terms === 'tempo') return downPayment;
    if (method === 'cash') {
      return Number(paidInput.replace(/[^0-9]/g, '')) || 0;
    }
    return grandTotal;
  }, [terms, method, paidInput, grandTotal, downPayment]);

  const outstanding = Math.max(0, grandTotal - paidAmount);

  // Validation
  const cashShort = terms === 'lunas' && method === 'cash' && paidAmount < grandTotal;
  const dueInvalid = terms === 'tempo' && (!dueDateMs || dueDateMs <= Date.now());
  // Only TEMPO needs a named customer — you can't extend credit to a walk-in.
  // A lunas cash/QRIS sale to a walk-in is the most common POS transaction and
  // must go through. Previously this was `!hasCustomer` unqualified, which
  // silently disabled the confirm button for EVERY customerless sale — found
  // by completing a real walk-in sale through the UI during the re-audit.
  const tempoBlocked = terms === 'tempo' && !hasCustomer;
  const canConfirm =
    grandTotal > 0 &&
    !cashShort &&
    !dueInvalid &&
    !tempoBlocked &&
    !loading;

  const handleSubmit = async () => {
    if (!canConfirm) return;
    if (terms === 'lunas') {
      const result = calculateChange(grandTotal, method, paidAmount);
      await onConfirm({
        paymentMethod: method,
        paidAmount: result.paidAmount,
        changeAmount: result.change,
        paymentTerms: 'lunas',
      });
      return;
    }
    // Tempo: paidAmount is the DP, change is 0 (no change given on DP),
    // remaining is the piutang tracked on the transaction.
    await onConfirm({
      paymentMethod: method,
      paidAmount: downPayment,
      changeAmount: 0,
      paymentTerms: 'tempo',
      dueDate: dueDateMs,
      downPayment,
    });
  };

  const dueDateLabel = dueDateMs
    ? format(dueDateMs, 'd MMM yyyy', { locale: idLocale })
    : '—';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Pembayaran"
      description={
        fulfillment === 'now'
          ? 'Customer ambil barang hari ini. Stock langsung dipotong.'
          : 'Customer ambil di lain hari. Stock di-hold sampai barang dikirim.'
      }
      size="md"
      closeOnBackdrop={!loading}
    >
      <div className="mb-5 p-4 rounded-lg bg-canvas border border-line flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-overline uppercase text-ink-subtle font-medium">
            Grand total
          </p>
          <div className="mt-1">
            <FitText className="text-metric font-display font-semibold text-ink tabular-nums">
              {formatIDR(grandTotal)}
            </FitText>
          </div>
        </div>
        <span
          className={cn(
            'text-caption font-medium px-3 py-1 rounded-full',
            fulfillment === 'now'
              ? 'bg-success/10 text-success'
              : 'bg-warning/10 text-warning',
          )}
        >
          {fulfillment === 'now' ? 'Ambil sekarang' : 'Kirim nanti'}
        </span>
      </div>

      {/* ── Lunas / Tempo toggle ── */}
      <div className="mb-5">
        <p className="text-small font-medium text-ink mb-2">Tipe pembayaran</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setTerms('lunas')}
            disabled={loading}
            className={cn(
              'p-3 rounded border text-left transition-all',
              terms === 'lunas'
                ? 'border-primary bg-primary/5'
                : 'border-line bg-surface hover:border-primary/30',
            )}
          >
            <p className="text-small font-semibold text-ink">Lunas</p>
            <p className="text-caption text-ink-subtle mt-0.5">
              Bayar lunas sekarang
            </p>
          </button>
          <button
            type="button"
            onClick={() => hasCustomer && setTerms('tempo')}
            disabled={loading || !hasCustomer}
            title={
              !hasCustomer
                ? 'Piutang butuh data customer (tidak boleh walk-in)'
                : undefined
            }
            className={cn(
              'p-3 rounded border text-left transition-all',
              terms === 'tempo'
                ? 'border-primary bg-primary/5'
                : 'border-line bg-surface hover:border-primary/30',
              !hasCustomer && 'opacity-50 cursor-not-allowed hover:border-line',
            )}
          >
            <p className="text-small font-semibold text-ink flex items-center gap-1.5">
              <CalendarClock size={13} /> Tempo
            </p>
            <p className="text-caption text-ink-subtle mt-0.5">
              {hasCustomer
                ? `Piutang · ${customerName ?? 'customer'} bayar nanti`
                : 'Butuh data customer'}
            </p>
          </button>
        </div>
        {!hasCustomer && (
          <p className="mt-2 text-caption text-warning flex items-center gap-1.5">
            <Info size={12} /> Walk-in tidak bisa piutang. Pilih customer dulu
            dari Cart panel.
          </p>
        )}
      </div>

      {/* ── Tempo panel: due date + DP ── */}
      {terms === 'tempo' && hasCustomer && (
        <div className="mb-5 p-3 rounded border border-line bg-canvas space-y-3">
          <div>
            <p className="text-small font-medium text-ink mb-2">Jatuh tempo</p>
            <div className="grid grid-cols-4 gap-1.5">
              {TERM_PRESETS.map((p) => (
                <button
                  key={p.days}
                  type="button"
                  onClick={() => setDuePreset(p.days)}
                  disabled={loading}
                  className={cn(
                    'px-2 py-1.5 rounded text-caption font-medium transition-colors',
                    duePreset === p.days
                      ? 'bg-primary text-white'
                      : 'bg-line/40 text-ink-subtle hover:bg-line/70',
                  )}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setDuePreset('custom')}
                disabled={loading}
                className={cn(
                  'px-2 py-1.5 rounded text-caption font-medium transition-colors',
                  duePreset === 'custom'
                    ? 'bg-primary text-white'
                    : 'bg-line/40 text-ink-subtle hover:bg-line/70',
                )}
              >
                Custom
              </button>
            </div>
            {duePreset === 'custom' && (
              <Input
                className="mt-2"
                type="date"
                value={customDueDate}
                min={format(addDays(new Date(), 1), 'yyyy-MM-dd')}
                onChange={(e) => setCustomDueDate(e.target.value)}
                disabled={loading}
              />
            )}
            {dueDateMs && (
              <p className="mt-2 text-caption text-ink-subtle">
                Jatuh tempo:{' '}
                <span className="font-medium text-ink">{dueDateLabel}</span>
              </p>
            )}
            {dueInvalid && (
              <p className="mt-1 text-caption text-danger flex items-center gap-1">
                <AlertTriangle size={11} /> Tanggal harus di masa depan
              </p>
            )}
          </div>

          <div>
            <Input
              label="Uang muka (DP, opsional)"
              type="text"
              inputMode="numeric"
              placeholder="0"
              value={downPaymentInput}
              onChange={(e) => setDownPaymentInput(e.target.value)}
              hint={
                downPayment > 0
                  ? `DP ${formatIDR(downPayment)} dari ${formatIDR(grandTotal)}`
                  : 'Boleh kosong — piutang penuh. Dicatat di halaman customer.'
              }
              disabled={loading}
              leftIcon={<span className="text-caption">Rp</span>}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 rounded border border-line bg-surface">
              <p className="text-overline uppercase text-ink-subtle font-medium">
                Sisa piutang
              </p>
              <p className="text-body font-semibold text-ink tabular-nums mt-0.5">
                {formatIDR(outstanding)}
              </p>
            </div>
            <div className="p-2 rounded border border-line bg-surface">
              <p className="text-overline uppercase text-ink-subtle font-medium">
                DP dibayar
              </p>
              <p className="text-body font-semibold text-ink tabular-nums mt-0.5">
                {formatIDR(downPayment)}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-5">
        <div>
          <p className="text-small font-medium text-ink mb-2">Metode bayar</p>
          <div className="grid grid-cols-3 gap-2">
            {METHODS.map((m) => {
              const Icon = m.icon;
              const active = m.value === method;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMethod(m.value)}
                  disabled={loading}
                  className={cn(
                    'flex flex-col items-center gap-1.5 p-3 rounded border text-center transition-all',
                    active
                      ? 'border-primary bg-primary/5'
                      : 'border-line bg-surface hover:border-primary/30',
                  )}
                >
                  <span
                    className={cn(
                      'w-9 h-9 rounded-full flex items-center justify-center',
                      active
                        ? 'bg-primary text-white'
                        : 'bg-line/40 text-ink-subtle',
                    )}
                  >
                    <Icon size={16} />
                  </span>
                  <p className="text-small font-medium text-ink">{m.label}</p>
                  <p className="text-caption text-ink-subtle leading-snug line-clamp-2">
                    {m.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {terms === 'lunas' && method === 'cash' && (
          <div>
            <Input
              label="Uang dibayar"
              type="text"
              inputMode="numeric"
              placeholder="0"
              value={paidInput}
              onChange={(e) => setPaidInput(e.target.value)}
              hint={
                cashShort
                  ? 'Nominal kurang dari grand total'
                  : 'Kosongkan untuk input cepat dari quick-amount di bawah.'
              }
              error={cashShort ? 'Nominal kurang' : undefined}
              disabled={loading}
              leftIcon={<span className="text-caption">Rp</span>}
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {QUICK_CASH_AMOUNTS.map((amt) => {
                const enough = amt >= grandTotal;
                return (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setPaidInput(formatNumber(amt))}
                    disabled={loading}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-caption font-medium transition-colors',
                      paidAmount === amt
                        ? 'bg-primary text-white'
                        : 'bg-line/40 text-ink-subtle hover:bg-line/70',
                    )}
                  >
                    {formatIDR(amt)}
                    {!enough && (
                      <span className="ml-1 text-warning" title="Kurang dari total">
                        ⚠
                      </span>
                    )}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setPaidInput(formatNumber(grandTotal))}
                disabled={loading}
                className="px-3 py-1.5 rounded-full text-caption font-medium bg-primary/10 text-primary hover:bg-primary/20"
              >
                Pas {formatIDR(grandTotal)}
              </button>
            </div>
          </div>
        )}

        {terms === 'lunas' && (
          <div
            className={cn(
              'p-3 rounded border flex items-center gap-3',
              paidAmount - grandTotal >= 0
                ? 'border-line bg-canvas'
                : 'border-danger bg-danger/5',
            )}
          >
            <div className="flex-1 min-w-0">
              <p className="text-overline uppercase text-ink-subtle font-medium">
                {paidAmount - grandTotal >= 0 ? 'Kembalian' : 'Kurang bayar'}
              </p>
              <div className="mt-1">
                <FitText
                  className={cn(
                    'text-subhead font-display font-semibold tabular-nums',
                    paidAmount - grandTotal >= 0 ? 'text-ink' : 'text-danger',
                  )}
                >
                  {formatIDR(Math.abs(paidAmount - grandTotal))}
                </FitText>
              </div>
            </div>
            {paidAmount - grandTotal < 0 && (
              <AlertTriangle
                size={20}
                className="text-danger flex-shrink-0"
              />
            )}
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={!canConfirm}
        >
          {terms === 'tempo'
            ? outstanding > 0
              ? `Simpan piutang · ${formatIDR(outstanding)}`
              : 'Simpan (lunas)'
            : method === 'cash'
              ? 'Confirm payment'
              : 'Complete transaction'}
        </Button>
      </div>
    </Modal>
  );
}
